import React from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock3,
  LoaderCircle,
  Save,
  XCircle,
  RotateCcw,
} from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";
import "./RenovationProjectLayout.css";
import { getDisplayStepStatus } from "../renovationProjectProgress";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const STAGES = [
  "PROPERTY_INFORMATION",
  "PRE_ASSESSMENT",
  "RENOVATION_PLAN",
  "ENERGY_PERFORMANCE_CERTIFICATE",
  "WORKS_BUDGET",
  "SUBSIDIES_GRANTS",
  "FINANCING",
  "MONITORING",
];

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function stepIcon(status) {
  if (status === "COMPLETED") {
    return CheckCircle2;
  }

  if (status === "IN_PROGRESS") {
    return Clock3;
  }

  if (status === "SKIPPED") {
    return XCircle;
  }

  return Circle;
}

function stepClass(status) {
  return String(status || "NOT_STARTED")
    .toLowerCase()
    .replaceAll("_", "-");
}

function stagePath(code) {
  const customPaths = {
    WORKS_BUDGET: "home-renovation",
  };

  if (customPaths[code]) {
    return customPaths[code];
  }

  return String(code || "")
    .toLowerCase()
    .replaceAll("_", "-");
}

export default function RenovationProjectLayout({ actor, basePath, backPath }) {
  const { t } = useTranslation("renovation-project-layout");

  const { projectId } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const [project, setProject] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [error, setError] = React.useState("");

  /* =========================================================
     SHARED STEP SAVE STATE
     ========================================================= */

  const stepSaveRegistrationRef = React.useRef(null);

  const stepDraftsRef = React.useRef({});

  const dirtyByPathRef = React.useRef({});

  const [hasStepSaveHandler, setHasStepSaveHandler] = React.useState(false);

  const [isStepDirty, setIsStepDirtyState] = React.useState(false);

  const [isStepSaving, setIsStepSaving] = React.useState(false);

  /* =========================================================
     LOAD SHARED PROJECT
     ========================================================= */

  const loadProject = React.useCallback(
    async ({ showLoading = true } = {}) => {
      if (!projectId) {
        setError(t("errors.loadFailed", "Failed to load project."));

        return;
      }

      if (showLoading) {
        setLoading(true);
      }

      setError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE}/api/projects/${projectId}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail || t("errors.loadFailed", "Failed to load project."),
          );
        }

        setProject(data);
      } catch (requestError) {
        setError(
          requestError?.message ||
            t("errors.loadFailed", "Failed to load project."),
        );
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [getAccessTokenSilently, projectId, t],
  );

  React.useEffect(() => {
    let active = true;

    async function run() {
      if (!active) {
        return;
      }

      await loadProject({
        showLoading: true,
      });
    }

    run();

    return () => {
      active = false;
    };
  }, [loadProject]);

  /* =========================================================
     REFRESH PROJECT
     ========================================================= */

  const refreshProject = React.useCallback(async () => {
    await loadProject({
      showLoading: false,
    });
  }, [loadProject]);

  const registerStepSaveHandler = React.useCallback((handler) => {
    const registration = {
      handler: typeof handler === "function" ? handler : null,
    };

    stepSaveRegistrationRef.current = registration;

    setHasStepSaveHandler(typeof handler === "function");

    return () => {
      if (stepSaveRegistrationRef.current === registration) {
        stepSaveRegistrationRef.current = null;

        setHasStepSaveHandler(false);
      }
    };
  }, []);

  const setStepDirty = React.useCallback(
    (dirty = true) => {
      const nextDirty = Boolean(dirty);

      dirtyByPathRef.current[location.pathname] = nextDirty;

      setIsStepDirtyState(nextDirty);
    },
    [location.pathname],
  );

  React.useEffect(() => {
    setIsStepDirtyState(Boolean(dirtyByPathRef.current[location.pathname]));
  }, [location.pathname]);

  /* =========================================================
     STEP DRAFT CACHE
     ========================================================= */

  const getStepDraft = React.useCallback((stepCode) => {
    if (!stepCode) {
      return null;
    }

    return stepDraftsRef.current[stepCode] || null;
  }, []);

  const setStepDraft = React.useCallback((stepCode, draft) => {
    if (!stepCode) {
      return;
    }

    stepDraftsRef.current = {
      ...stepDraftsRef.current,
      [stepCode]: draft,
    };
  }, []);

  const clearStepDraft = React.useCallback((stepCode) => {
    if (!stepCode) {
      return;
    }

    const nextDrafts = {
      ...stepDraftsRef.current,
    };

    delete nextDrafts[stepCode];

    stepDraftsRef.current = nextDrafts;
  }, []);

  /* =========================================================
     SAVE CURRENT STEP
     ========================================================= */

  const saveCurrentStep = React.useCallback(async () => {
    const handler = stepSaveRegistrationRef.current?.handler;

    if (!handler || !isStepDirty) {
      return true;
    }

    if (isStepSaving) {
      return false;
    }

    setIsStepSaving(true);

    try {
      await handler();

      dirtyByPathRef.current[location.pathname] = false;
      setIsStepDirtyState(false);

      await refreshProject();

      showToast({
        type: "success",

        title: t("save.successTitle", "Changes saved"),

        message: t(
          "save.successMessage",
          "Your changes have been saved successfully.",
        ),
      });

      return true;
    } catch (saveRequestError) {
      dirtyByPathRef.current[location.pathname] = true;
      setIsStepDirtyState(true);

      showToast({
        type: "error",

        title: t("save.errorTitle", "Save failed"),

        message:
          saveRequestError?.message ||
          t("save.failed", "Failed to save changes."),
      });

      return false;
    } finally {
      setIsStepSaving(false);
    }
  }, [
    isStepDirty,
    isStepSaving,
    location.pathname,
    refreshProject,
    showToast,
    t,
  ]);

  /* =========================================================
     ORDERED STEPS
     ========================================================= */

  const orderedSteps = React.useMemo(() => {
    const steps = project?.steps || [];

    return STAGES.map((code, index) => {
      const found = steps.find((step) => step.step_code === code);

      return (
        found || {
          step_code: code,
          step_order: index + 1,
          status: "NOT_STARTED",
          is_optional: false,
        }
      );
    });
  }, [project]);

  /* =========================================================
     ROUTE HELPERS
     ========================================================= */

  const goToStage = React.useCallback(
    (stepCode) => {
      if (!project?.id) {
        return;
      }

      navigate(`${basePath}/${project.id}/${stagePath(stepCode)}`);
    },
    [basePath, navigate, project?.id],
  );

  /* =========================================================
     LOADING
     ========================================================= */

  if (loading) {
    return (
      <main className="renovation-project-layout">
        <div className="renovation-project-layout__loading">
          {t("loading", "Loading project...")}
        </div>
      </main>
    );
  }

  /* =========================================================
     ERROR / ACCESS DENIED
     ========================================================= */

  if (error || !project) {
    return (
      <main className="renovation-project-layout">
        <div className="renovation-project-layout__error">
          <div className="renovation-project-layout__error-icon">
            <AlertCircle size={30} />
          </div>

          <h2>{t("errors.title", "Project unavailable")}</h2>

          <p>{error || t("errors.loadFailed", "Failed to load project.")}</p>

          <div className="renovation-project-layout__error-actions">
            <button
              type="button"
              className="renovation-project-layout__retry-btn"
              onClick={() =>
                loadProject({
                  showLoading: true,
                })
              }
            >
              <RotateCcw size={16} />

              {t("buttons.retry", "Try again")}
            </button>

            {backPath ? (
              <button
                type="button"
                className="renovation-project-header__back-btn"
                onClick={() => navigate(backPath)}
              >
                <ArrowLeft size={16} />

                {actor === "OSS"
                  ? t("buttons.backToProjects", "Back to projects")
                  : t("buttons.backToDashboard", "Back to dashboard")}
              </button>
            ) : null}
          </div>
        </div>
      </main>
    );
  }

  /* =========================================================
     ACCESS INFORMATION FROM BACKEND
     ========================================================= */

  const viewerRole = project?.viewer?.role || "";

  return (
    <main className="renovation-project-layout">
      {/* =====================================================
          PROJECT HEADER
          ===================================================== */}

      <section className="renovation-project-header">
        <div>
          <h1>
            {project.code} — {project.property_address || project.title}
          </h1>

          <p>
            {t("lastUpdate", "Last update")}{" "}
            <strong>{formatDateTime(project.updated_at)}</strong>
          </p>
        </div>

        <div className="renovation-project-header__actions">
          {hasStepSaveHandler && (
            <div className="renovation-project-header__save-area">
              <button
                type="button"
                className={
                  isStepDirty
                    ? "renovation-project-header__save-btn is-dirty"
                    : "renovation-project-header__save-btn"
                }
                onClick={saveCurrentStep}
                disabled={isStepSaving || !isStepDirty}
              >
                {isStepSaving ? (
                  <LoaderCircle
                    size={16}
                    className="renovation-project-save-spinner"
                  />
                ) : (
                  <Save size={16} />
                )}

                {isStepSaving
                  ? t("save.saving", "Saving...")
                  : t("save.button", "Save changes")}
              </button>
            </div>
          )}

          {backPath ? (
            <button type="button" onClick={() => navigate(backPath)}>
              <ArrowLeft size={16} />

              {actor === "OSS"
                ? t("buttons.backToProjects", "Back to projects")
                : t("buttons.backToDashboard", "Back to dashboard")}
            </button>
          ) : null}
        </div>
      </section>

      {/* =====================================================
          WORKFLOW BODY
          ===================================================== */}

      <section className="renovation-project-layout__body">
        <aside className="renovation-project-sidebar">
          <h2>{t("stages.title", "Project stages")}</h2>

          <nav>
            {orderedSteps.map((step) => {
              const displayStatus = getDisplayStepStatus(step, project);

              const Icon = stepIcon(displayStatus);

              const active = location.pathname.endsWith(
                `/${stagePath(step.step_code)}`,
              );

              return (
                <button
                  key={step.step_code}
                  type="button"
                  className={
                    active
                      ? `renovation-project-stage is-active is-${stepClass(
                          displayStatus,
                        )}`
                      : `renovation-project-stage is-${stepClass(
                          displayStatus,
                        )}`
                  }
                  onClick={() => goToStage(step.step_code)}
                >
                  <Icon size={15} />

                  <span>
                    {t(`steps.${step.step_code}`, step.title || step.step_code)}
                  </span>

                  <small>
                    {t("stages.step", "Step {{number}}", {
                      number: step.step_order,
                    })}

                    {step.is_optional
                      ? ` · ${t("stages.optional", "Optional")}`
                      : ""}
                  </small>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="renovation-project-outlet">
          <Outlet
            context={{
              project,
              steps: orderedSteps,
              actor,
              basePath,
              backPath,
              viewerRole,
              viewer: project.viewer || {},
              refreshProject,
              goToStage,
              registerStepSaveHandler,
              setStepDirty,
              isStepDirty,
              isStepSaving,
              saveCurrentStep,
              getStepDraft,
              setStepDraft,
              clearStepDraft,
            }}
          />
        </section>
      </section>
    </main>
  );
}
